import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useTheme } from '@/hooks/use-theme';
import { useAuth } from '@/lib/auth';
import { hasCapability } from '@/lib/rbac';
import { useTranslation } from 'react-i18next';
import { Home, FileText, Ban, ClipboardList, Inbox, Users, Plus, Sun, LogOut } from 'lucide-react';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { toggle } = useTheme();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onOpenChange]);

  const handleSelect = (value: string) => {
    onOpenChange(false);
    switch (value) {
      case 'overview':
        navigate('/');
        break;
      case 'licenses':
        navigate('/licenses');
        break;
      case 'blacklist':
        navigate('/blacklist');
        break;
      case 'audit':
        navigate('/audit');
        break;
      case 'requests':
        navigate('/requests');
        break;
      case 'operators':
        navigate('/operators');
        break;
      case 'create-license':
        navigate('/licenses/new');
        break;
      case 'toggle-theme':
        toggle();
        break;
      case 'sign-out':
        void signOut();
        break;
    }
  };

  const can = (capability: Parameters<typeof hasCapability>[1]) =>
    !!user && hasCapability(user.role, capability);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('shell.palette.placeholder')} />
      <CommandList>
        <CommandEmpty>{t('shell.palette.empty')}</CommandEmpty>
        <CommandGroup heading={t('shell.palette.navigation')}>
          {can('viewOverview') && (
            <CommandItem onSelect={() => handleSelect('overview')}>
              <Home className="mr-2 h-4 w-4" />
              {t('shell.palette.goOverview')}
            </CommandItem>
          )}
          {can('manageLicenses') && (
            <CommandItem onSelect={() => handleSelect('licenses')}>
              <FileText className="mr-2 h-4 w-4" />
              {t('shell.palette.goLicenses')}
            </CommandItem>
          )}
          {can('viewBlacklist') && (
            <CommandItem onSelect={() => handleSelect('blacklist')}>
              <Ban className="mr-2 h-4 w-4" />
              {t('shell.palette.goBlacklist')}
            </CommandItem>
          )}
          {can('viewAudit') && (
            <CommandItem onSelect={() => handleSelect('audit')}>
              <ClipboardList className="mr-2 h-4 w-4" />
              {t('shell.palette.goAudit')}
            </CommandItem>
          )}
          {(can('submitLicenseRequests') || can('reviewLicenseRequests')) && (
            <CommandItem onSelect={() => handleSelect('requests')}>
              <Inbox className="mr-2 h-4 w-4" />
              {t('shell.palette.goRequests')}
            </CommandItem>
          )}
          {can('manageUsers') && (
            <CommandItem onSelect={() => handleSelect('operators')}>
              <Users className="mr-2 h-4 w-4" />
              {t('shell.palette.goOperators')}
            </CommandItem>
          )}
        </CommandGroup>
        <CommandGroup heading={t('shell.palette.actions')}>
          {can('manageLicenses') && (
            <CommandItem onSelect={() => handleSelect('create-license')}>
              <Plus className="mr-2 h-4 w-4" />
              {t('shell.palette.createLicense')}
            </CommandItem>
          )}
          <CommandItem onSelect={() => handleSelect('toggle-theme')}>
            <Sun className="mr-2 h-4 w-4" />
            {t('shell.palette.toggleTheme')}
          </CommandItem>
          <CommandItem onSelect={() => handleSelect('sign-out')}>
            <LogOut className="mr-2 h-4 w-4" />
            {t('shell.palette.signOut')}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
